/*
 * FURVIBE — "Complete the set" (sections/fv-product-bundle.liquid).
 * The product being viewed follows the variant chosen in fv-product (the 'fv:variant' event, with the
 * picker's hidden id field as a fallback); paired pieces can be left out or switched to another
 * color / size. Total, free-shipping status (counting what is already in the bag) and the button
 * update live. One POST to cart/add.js adds every chosen line; CartLinesUpdateEvent is dispatched
 * only after it succeeds, so Horizon's cart drawer refreshes and opens like a normal add.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const ADD_URL = (window.Theme && Theme.routes && Theme.routes.cart_add_url) || ROOT + 'cart/add.js';
const CART_PAGE = (window.Theme && Theme.routes && Theme.routes.cart_url) || ROOT + 'cart';
const CART_URL = CART_PAGE + '.js';
const PICKER_ID = 'fv-product [data-fv-id]';

/* "Stone / 6 in (15.5 cm)" -> "Stone / 6 in": drop a trailing metric conversion from each part. */
const shortTitle = (t) =>
  String(t || '')
    .split(' / ')
    .map((part) => part.replace(/\s*\([^()]*\d\s?(?:cm|mm|m|ml|kg|g)\)\s*$/i, ''))
    .join(' / ');

class FvBundle extends HTMLElement {
  connectedCallback() {
    if (!this._ready) this.init();
    if (!this.lines) return;
    this._ac = new AbortController();
    const signal = this._ac.signal;
    // Capture phase: heard even if fv-product dispatches its event without bubbling.
    document.addEventListener('fv:variant', (e) => this.onVariant(e), { capture: true, signal });
    document.addEventListener('change', (e) => this.onPicker(e), { capture: true, signal });
    document.addEventListener('shopify:cart:lines-update', (e) => this.onCartUpdate(e), { signal });
    window.addEventListener('pageshow', (e) => {
      if (e.persisted) this.syncFromPicker();
    }, { signal });
    this.syncFromPicker();
  }

  disconnectedCallback() {
    if (this._ac) this._ac.abort();
    clearTimeout(this._doneTimer);
  }

  init() {
    this._ready = true;
    const json = this.querySelector('[data-fv-bnd-json]');
    if (!json) return;
    try {
      this.data = JSON.parse(json.textContent);
    } catch (e) {
      return;
    }
    this.currency = this.dataset.currency || (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
    this.fmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: this.currency });
    this.threshold = Number(this.data.threshold) || 0;
    this.cartCents = Number(this.data.cart) || 0;
    this.mainVariants = Array.isArray(this.data.main) ? this.data.main : [];
    this.form = this.querySelector('[data-fv-bnd-form]');
    this.btn = this.querySelector('[data-fv-bnd-atc]');
    this.err = this.querySelector('[data-fv-bnd-error]');
    this.live = this.querySelector('[data-fv-bnd-live]');
    this.lines = [...this.querySelectorAll('[data-fv-bnd-line]')].map((el) => ({
      el,
      main: el.hasAttribute('data-fv-bnd-main'),
      cb: el.querySelector('[data-fv-bnd-cb]'),
      select: el.querySelector('[data-fv-bnd-select]'),
      price: el.querySelector('[data-fv-bnd-price]'),
      item: this.querySelector(`[data-fv-bnd-item="${el.dataset.fvBndLine}"]`),
    }));
    this.main = this.lines.find((l) => l.main) || null;
    this.mainAvailable = !this.main || this.main.el.dataset.available !== 'false';
    this.justAdded = false;

    // Without JS the plain form adds the whole set, so pair checkboxes start fixed on.
    this.lines.forEach((l) => {
      if (!l.main && l.cb) l.cb.disabled = false;
    });

    this.addEventListener('change', (e) => this.onLineChange(e));
    if (this.form) {
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.add();
      });
    }
    this.classList.add('is-ready');
    this.render();
  }

  money(cents) {
    return this.fmt.format((Number(cents) || 0) / 100);
  }

  moneyShort(cents) {
    return this.money(cents).replace(/\.00$/, '');
  }

  /* ------------------------------------------------------------ the product being viewed */
  syncFromPicker() {
    const field = document.querySelector(PICKER_ID);
    if (field && field.value) this.setMain(field.value);
  }

  onVariant(e) {
    const v = e.detail && e.detail.variant;
    if (v && v.id != null) this.setMain(v.id);
  }

  onPicker(e) {
    const t = e.target;
    if (t instanceof Element && t.matches(PICKER_ID)) this.setMain(t.value);
  }

  setMain(id) {
    if (!this.main) return;
    const key = String(id);
    const v = this.mainVariants.find((x) => String(x.id) === key);
    if (!v) return;
    const el = this.main.el;
    if (el.dataset.vid === key && this._synced) return;
    this._synced = true;
    el.dataset.vid = key;
    el.dataset.price = String(v.price);
    if (this.main.price) this.main.price.textContent = this.money(v.price);
    const name = el.querySelector('[data-fv-bnd-main-variant]');
    if (name) name.textContent = shortTitle(v.title);
    const idField = el.querySelector('[data-fv-bnd-main-id]');
    if (idField) idField.value = key;
    this.mainAvailable = !!v.available;
    el.classList.toggle('is-soldout', !v.available);
    const note = el.querySelector('[data-fv-bnd-soldout]');
    if (note) note.hidden = !!v.available;
    if (v.img) this.swapImage(this.main, v.img);
    this.justAdded = false;
    this.render();
  }

  /* ------------------------------------------------------------ paired pieces */
  onLineChange(e) {
    const t = e.target;
    if (!(t instanceof Element)) return;
    const line = this.lines.find((l) => l.el.contains(t));
    if (!line || line.main) return;
    if (t.matches('[data-fv-bnd-select]')) {
      const opt = t.selectedOptions && t.selectedOptions[0];
      if (!opt) return;
      line.el.dataset.vid = opt.value;
      line.el.dataset.price = opt.dataset.price || line.el.dataset.price;
      if (line.price) line.price.textContent = this.money(line.el.dataset.price);
      if (opt.dataset.img) this.swapImage(line, opt.dataset.img);
      // Choosing a color or size means the shopper wants the piece.
      if (line.cb && !line.cb.checked) line.cb.checked = true;
    } else if (!t.matches('[data-fv-bnd-cb]')) {
      return;
    }
    this.justAdded = false;
    if (this.err) this.err.hidden = true;
    this.render();
    this.announce();
  }

  /* Swap a tile's photo once the new one is loaded, so the tile never flashes empty. */
  swapImage(line, url) {
    const tile = line.item && line.item.querySelector('[data-fv-bnd-img]');
    const img = tile && tile.querySelector('img.fv-bnd__img');
    if (!img || !url || tile.dataset.fvBndImg === url) return;
    tile.dataset.fvBndImg = url;
    const apply = () => {
      if (tile.dataset.fvBndImg !== url) return;
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.src = url;
    };
    const pre = new Image();
    pre.onload = apply;
    pre.onerror = apply;
    pre.src = url;
  }

  /* ------------------------------------------------------------ totals + free shipping */
  chosen() {
    return this.lines.filter((l) => l.main || (l.cb && l.cb.checked));
  }

  shipState() {
    const T = this.threshold;
    if (!T) return null;
    const B = Math.max(0, this.cartCents || 0);
    const S = this.total;
    if (this.justAdded) {
      if (B >= T) return { free: true, p: 1, text: 'Your bag ships free' };
      return { free: false, p: B / T, text: `Your bag is ${this.moneyShort(T - B)} away from free US shipping` };
    }
    if (S >= T) return { free: true, p: 1, text: 'Your set ships free' };
    if (B > 0 && B + S >= T) return { free: true, p: 1, text: 'With your bag, this set ships free' };
    return { free: false, p: (B + S) / T, text: `Add ${this.moneyShort(T - B - S)} more for free US shipping` };
  }

  render() {
    const chosen = this.chosen();
    this.lines.forEach((l) => {
      const on = chosen.includes(l);
      l.el.classList.toggle('is-off', !on);
      if (l.item) l.item.classList.toggle('is-off', !on);
    });
    this.total = chosen.reduce((sum, l) => sum + (Number(l.el.dataset.price) || 0), 0);
    this.count = chosen.length;

    const totalEl = this.querySelector('[data-fv-bnd-total]');
    if (totalEl) totalEl.textContent = this.money(this.total);
    const countEl = this.querySelector('[data-fv-bnd-count]');
    if (countEl) countEl.textContent = `${this.count} ${this.count === 1 ? 'piece' : 'pieces'}`;

    if (this.btn) {
      this.btn.disabled = !this.mainAvailable;
      const label = this.btn.querySelector('[data-fv-bnd-label]');
      if (label && !this.btn.classList.contains('is-done')) {
        label.textContent = !this.mainAvailable ? 'Sold out' : this.count > 1 ? 'Add the set to bag' : 'Add to bag';
      }
      const price = this.btn.querySelector('[data-fv-bnd-atc-price]');
      if (price) price.textContent = this.money(this.total);
    }

    const ship = this.querySelector('[data-fv-bnd-ship]');
    const state = this.shipState();
    if (ship && state) {
      ship.classList.toggle('is-free', state.free);
      const text = ship.querySelector('[data-fv-bnd-ship-text]');
      if (text) text.textContent = state.text;
      const fill = ship.querySelector('[data-fv-bnd-fill]');
      if (fill) fill.style.setProperty('--p', String(Math.max(0, Math.min(1, state.p)).toFixed(3)));
    }
    this.shipText = state ? state.text : '';
  }

  announce(prefix) {
    if (!this.live) return;
    const pieces = `${this.count} ${this.count === 1 ? 'piece' : 'pieces'}`;
    const msg = `${prefix ? prefix + ' ' : ''}${pieces}, ${this.money(this.total)}.${this.shipText ? ' ' + this.shipText + '.' : ''}`;
    // Clear first so the same sentence is read again.
    this.live.textContent = '';
    requestAnimationFrame(() => (this.live.textContent = msg));
  }

  /* Keep the free-shipping line honest when the bag changes anywhere on the page. */
  onCartUpdate(e) {
    const promise = e && e.promise;
    if (!promise || typeof promise.then !== 'function') return;
    promise
      .then((result) => {
        const amount = result && result.cart && result.cart.cost && result.cart.cost.totalAmount && result.cart.cost.totalAmount.amount;
        const n = Number(amount);
        if (amount == null || !Number.isFinite(n)) return;
        this.cartCents = Math.round(n * 100);
        this.render();
      })
      .catch(() => {});
  }

  /* ------------------------------------------------------------ add to bag */
  async add() {
    if (this._busy || !this.btn || this.btn.disabled) return;
    const items = this.chosen().map((l) => ({ id: Number(l.el.dataset.vid), quantity: 1 })).filter((it) => it.id);
    if (!items.length) return;
    this._busy = true;
    clearTimeout(this._doneTimer);
    this.btn.classList.remove('is-done');
    this.btn.classList.add('is-busy');
    this.btn.setAttribute('aria-busy', 'true');
    if (this.err) this.err.hidden = true;

    const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

    const settle = () => {
      this.btn.classList.remove('is-busy');
      this.btn.removeAttribute('aria-busy');
      this._busy = false;
    };
    const fail = (msg) => {
      if (this.err) {
        this.err.textContent = msg;
        this.err.hidden = false;
      }
      settle();
    };

    let res;
    let json;
    try {
      res = await fetch(ADD_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      json = await res.json();
    } catch (e) {
      this.dispatchEvent(new CartErrorEvent({ error: 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' }));
      return fail('Connection problem. Please try again.');
    }
    if (!res.ok || (json && json.status)) {
      const msg = (json && (json.description || json.message)) || 'Could not add the set to your bag. Please try again.';
      this.dispatchEvent(new CartErrorEvent({ error: msg, code: 'INVALID', detail: { description: json && json.description, errors: json && json.errors } }));
      return fail(msg);
    }

    // Success only: Horizon's drawer re-renders from the sections and opens; the header count updates.
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((it) => ({ merchandiseId: String(it.id), quantity: it.quantity })),
      promise: deferred.promise,
    }));
    const itemCount = items.reduce((n, it) => n + it.quantity, 0);
    try {
      const cart = await fetch(CART_URL, { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then((r) => r.json());
      if (Number.isFinite(Number(cart.total_price))) this.cartCents = Number(cart.total_price);
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, sections: json.sections, source: 'fv-bundle', itemCount, didError: false },
      });
    } catch (e) {
      deferred.resolve({ cart: null, detail: { sections: json.sections, source: 'fv-bundle', itemCount, didError: false } });
    }

    const drawer = document.querySelector('theme-drawer#cart-drawer');
    if (drawer && !drawer.querySelector('cart-drawer-component[auto-open]') && typeof drawer.open === 'function') {
      setTimeout(() => drawer.open(), 150);
    } else if (!drawer && !document.querySelector('cart-drawer-component')) {
      window.location.href = CART_PAGE;
      return;
    }

    settle();
    this.justAdded = true;
    this.btn.classList.add('is-done');
    const label = this.btn.querySelector('[data-fv-bnd-label]');
    if (label) label.textContent = 'Added';
    this.render();
    this.announce('Added to bag:');
    this._doneTimer = setTimeout(() => {
      this.btn.classList.remove('is-done');
      this.render();
    }, 2000);
  }
}

if (!customElements.get('fv-bundle')) customElements.define('fv-bundle', FvBundle);
